import React, { useEffect, useRef, useState } from "react";
import { FootprintsIcon as Footprints, CheckCircleIcon as CheckCircle, XIcon as X } from "@phosphor-icons/react";
import api from "@/lib/api";
import useLeaflet from "@/lib/useLeaflet";

/**
 * Inline in-app live-walk map for the buyer.
 *
 * Renders inside the buyer's authenticated /my-bookings row when
 * an active or recently-ended walk is on file. Polls
 * /api/bookings/{id}/walk (auth required) every 10s while active
 * and once after end to grab the summary.
 *
 * No public no-auth share endpoint exists — that decision is the
 * core privacy guarantee of this feature. If you ever consider
 * re-introducing one, read /app/memory/CHANGELOG.md iteration on
 * the "in-app only" lock first.
 */
const POLL_MS = 10_000;
const DEFAULT_CENTER = [37.7749, -122.4194];

function haversineKm(a, b) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(h));
}

function pathDistanceKm(points) {
  if (!points || points.length < 2) return 0;
  let km = 0;
  for (let i = 1; i < points.length; i += 1) {
    km += haversineKm(
      [points[i - 1].lat, points[i - 1].lng],
      [points[i].lat, points[i].lng],
    );
  }
  return km;
}

function fmtMinSec(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

export default function BuyerLiveWalkMap({ bookingId, onClose }) {
  const [walk, setWalk] = useState(null);
  const [error, setError] = useState(null);

  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const polylineRef = useRef(null);
  const markerRef = useRef(null);

  useLeaflet((L) => {
    if (mapRef.current || !mapElRef.current) return;
    const map = L.map(mapElRef.current, {
      center: DEFAULT_CENTER,
      zoom: 13,
      minZoom: 3,
      maxZoom: 19,
      zoomControl: true,
      attributionControl: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
  });

  useEffect(() => {
    if (!bookingId) return undefined;
    let cancelled = false;
    let timer = null;
    const fetchOnce = async () => {
      try {
        const { data } = await api.get(`/bookings/${bookingId}/walk`);
        if (cancelled) return;
        const w = data?.walk || null;
        setWalk(w);
        setError(null);
        if (w && w.status === "ended" && timer) {
          clearInterval(timer);
          timer = null;
        }
      } catch (err) {
        if (cancelled) return;
        setError("Couldn't load the walk right now.");
        if (timer) clearInterval(timer);
      }
    };
    fetchOnce();
    timer = setInterval(fetchOnce, POLL_MS);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [bookingId]);

  // Draw polyline + marker whenever points change (active walk only).
  useEffect(() => {
    const L = typeof window !== "undefined" ? window.L : null;
    const map = mapRef.current;
    if (!L || !map) return;

    const pts = (walk?.points || []).map((p) => [p.lat, p.lng]);
    if (pts.length === 0) {
      if (polylineRef.current) {
        map.removeLayer(polylineRef.current);
        polylineRef.current = null;
      }
      if (markerRef.current) {
        map.removeLayer(markerRef.current);
        markerRef.current = null;
      }
      return;
    }

    if (polylineRef.current) {
      polylineRef.current.setLatLngs(pts);
    } else {
      polylineRef.current = L.polyline(pts, {
        color: "#C9A227",
        weight: 5,
        opacity: 0.92,
        lineCap: "round",
        lineJoin: "round",
      }).addTo(map);
    }

    const last = pts[pts.length - 1];
    if (markerRef.current) {
      markerRef.current.setLatLng(last);
    } else {
      const dotIcon = L.divIcon({
        className: "walk-share-marker",
        iconSize: [22, 22],
        html:
          '<div style="width:22px;height:22px;border-radius:50%;background:#C1272D;box-shadow:0 0 0 6px rgba(193,39,45,0.25);border:2px solid #ffffff;"></div>',
      });
      markerRef.current = L.marker(last, { icon: dotIcon }).addTo(map);
    }

    if (pts.length === 1) {
      map.setView(last, 16);
    } else if (pts.length > 1) {
      map.fitBounds(polylineRef.current.getBounds(), { padding: [40, 40] });
    }
  }, [walk]);

  // Plot the summary's start + end pins for ended walks.
  useEffect(() => {
    const L = typeof window !== "undefined" ? window.L : null;
    const map = mapRef.current;
    if (!L || !map || !walk || walk.status !== "ended" || !walk.summary) return;
    const start = walk.summary.start_pin;
    const end = walk.summary.end_pin;
    if (!start || !end) return;
    const bounds = L.latLngBounds([
      [start.lat, start.lng],
      [end.lat, end.lng],
    ]);
    L.marker([start.lat, start.lng]).bindPopup("Start (approx.)").addTo(map);
    L.marker([end.lat, end.lng]).bindPopup("End (approx.)").addTo(map);
    map.fitBounds(bounds, { padding: [60, 60], maxZoom: 14 });
  }, [walk]);

  const isActive = walk?.status === "active";
  const distanceKm = isActive
    ? pathDistanceKm(walk?.points || [])
    : (walk?.summary?.distance_m || 0) / 1000;
  const distanceMi = distanceKm * 0.621371;

  return (
    <div
      className="mt-3 rounded-xl border border-gold/40 bg-white overflow-hidden"
      data-testid={`buyer-walk-map-${bookingId}`}
    >
      <div className="flex items-center justify-between px-4 py-3 bg-ink text-white">
        <div className="flex items-center gap-2">
          <Footprints size={18} weight="duotone" className="text-gold-light" />
          <p className="font-spectral text-[15px] tracking-tight">
            {isActive ? "Walk in progress" : "Walk completed"}
            {walk?.dog_name ? <span className="text-white/70"> · {walk.dog_name}</span> : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isActive ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 text-emerald-200 px-2.5 py-1 text-[11px] font-bold border border-emerald-400/40">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-80 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-300" />
              </span>
              Live
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 text-white/85 px-2.5 py-1 text-[11px] font-bold border border-white/20">
              <CheckCircle size={12} weight="fill" /> Ended
            </span>
          )}
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="text-white/65 hover:text-white"
              aria-label="Close walk map"
              data-testid={`buyer-walk-map-close-${bookingId}`}
            >
              <X size={16} weight="bold" />
            </button>
          ) : null}
        </div>
      </div>

      <div
        ref={mapElRef}
        style={{ height: "min(50vh, 380px)" }}
        className="w-full bg-cream"
        data-testid={`buyer-walk-map-canvas-${bookingId}`}
      />

      <div className="grid grid-cols-3 divide-x divide-[#E2DAC2] border-t border-[#E2DAC2] text-center">
        <div className="px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-gold-dark font-bold">Distance</p>
          <p className="font-spectral text-lg text-ink mt-0.5 font-tabular">{distanceMi.toFixed(2)} mi</p>
        </div>
        <div className="px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-gold-dark font-bold">Duration</p>
          <p className="font-spectral text-lg text-ink mt-0.5 font-tabular">
            {isActive
              ? walk?.started_at
                ? fmtMinSec((Date.now() - new Date(walk.started_at).getTime()) / 1000)
                : "—"
              : fmtMinSec(walk?.summary?.duration_s || 0)}
          </p>
        </div>
        <div className="px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-gold-dark font-bold">GPS pts</p>
          <p className="font-spectral text-lg text-ink mt-0.5 font-tabular">{walk?.point_count ?? 0}</p>
        </div>
      </div>

      {/* Mid-walk photo updates from the walker */}
      {(walk?.photos || []).length > 0 && (
        <div className="px-4 py-3 border-t border-[#EAEAEA]" data-testid="buyer-walk-photos">
          <p className="text-[10px] uppercase tracking-[0.18em] text-gold-dark font-bold mb-2">
            📸 Photos from the walk
          </p>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {walk.photos.map((p, i) => (
              <a key={i} href={p.url} target="_blank" rel="noreferrer" className="shrink-0">
                <img
                  src={p.url}
                  alt={`Walk update ${i + 1}`}
                  className="h-24 w-24 object-cover rounded-lg border border-[#EAEAEA]"
                  loading="lazy"
                />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Venue disclaimer — pinned inside every live view so the buyer
          can't claim they weren't told Armstrong is a dumb pipe.        */}
      <div className="px-4 py-2.5 bg-cream/60 border-t border-[#E2DAC2] text-[11.5px] text-[#6B6B6B] leading-snug">
        Armstrong K9 Registry transmits location data between you and the
        walker. <strong>Armstrong does not monitor this walk</strong> and is
        not responsible for safety, accuracy, or continuity of the signal.
        {!isActive ? (
          <> The walk recap is visible here for 24 hours, then permanently deleted.</>
        ) : (
          <> GPS pauses if the walker&rsquo;s phone is locked or the app is backgrounded.</>
        )}
      </div>

      {error && (
        <div className="px-4 py-2 text-[12px] text-amber-700 bg-amber-50 border-t border-amber-200">
          {error}
        </div>
      )}
    </div>
  );
}
